import {
  Cell,
  NonEditableCell,
  NumberCell,
  TextCell,
} from '@silevis/reactgrid';
import { useMemo } from 'react';
import { useTheme } from '@mui/material';
import addCell from '../helpers/addCell';
import usePlanStore from '../../../../../../../../../../../store/planStore';
import useHandleUpdateDataRows from './useHandleUpdateDataRows';
import usePlanTotals from './usePlanTotals';

const useAddCopyDataRows = (cells: Cell[]) => {
  const { countryForms, selectedCountry } = usePlanStore();
  const tvConfig = countryForms[selectedCountry!]?.offline?.tv;
  const weeks = tvConfig?.grpTable?.weeks ?? [];

  const theme = useTheme();

  const { planTotals } = usePlanTotals(weeks);

  const memoizedWeeks = useMemo(() => weeks, [weeks]);

  const tableData = memoizedWeeks.map((week) => week.copy.value);

  const stableRows = 3;
  const stableColumns = 2;

  const { handleCellUpdate } = useHandleUpdateDataRows(tvConfig);

  tableData[0]?.forEach((copy, rowIndex) => {
    addCell({
      cells,
      rowIndex: rowIndex + stableRows,
      colIndex: 0,
      Template: NonEditableCell,
      value: copy.name,
      sx: {
        color: theme.palette.grpTable.text.main,
        fontWeight: 500,
        fontSize: '12px',
        background: theme.palette.grpTable.background.default,
        textAlign: 'center',
      },
    });
    addCell({
      cells,
      rowIndex: rowIndex + stableRows,
      colIndex: 1,
      Template: NonEditableCell,
      value: +(copy.name?.match(/^(\d+)/)?.[0] ?? 0),
      sx: {
        color: theme.palette.grpTable.text.main,
        fontWeight: 700,
        fontSize: '14px',
        background: theme.palette.grpTable.background.default,
      },
    });

    const newCells = memoizedWeeks.map((_, weekIndex) => {
      const cellValue = tableData[weekIndex][rowIndex]?.value;
      return {
        rowIndex: rowIndex + stableRows,
        colIndex: weekIndex + stableColumns,
        Template: NumberCell,
        props: {
          value: cellValue,
          onValueChanged: (newValue: number) =>
            handleCellUpdate(rowIndex, weekIndex + 2, newValue, (v) => v),
          style: {
            color: theme.palette.grpTable.text.main,
            fontWeight: 700,
            fontSize: '14px',
          },
        },
      };
    });

    cells.push(...newCells);

    addCell({
      cells,
      rowIndex: rowIndex + stableRows,
      colIndex: weeks.length + stableColumns,
      Template: NonEditableCell,
      value: planTotals[rowIndex],
      sx: {
        color: theme.palette.grpTable.text.light,
        fontWeight: 700,
        fontSize: '14px',
        background: theme.palette.grpTable.background.default,
      },
    });
  });
};

export default useAddCopyDataRows;
